import { ReactElement, useState } from "react";

import { Button } from "@justworkshr/milo-core";
import AuthPageTemplate from "../authPageTemplate/AuthPageTemplate";
import FactorList from "global-components/mfa/components/factorList/FactorList";
import Username from "./components/username/Username";

import { title, description } from "global-components/mfa/constants/mfaCopy";

import DisableSetupFactorContext from "global-components/mfa/contexts/DisableFactorSetupContext";

import {
  AuthTransaction,
  StatusEnum,
} from "pages/register/types/authTransaction";
import { ParsedFactorData } from "pages/register/types/parsedTransactionFactorData";
import { FactorKey } from "types/factor/factor";
import useGetFeatureFlags from "hooks/useGetFeatureFlags";
import { OktaError } from "types/oktaError/oktaError";

import useCancelMfaEnrollment from "pages/register/hooks/useCancelMfaEnrollment";
import useSkipMfaEnrollment from "pages/register/hooks/useSkipMfaEnrollment";

import buildFactorLists from "global-components/mfa/util/buildFactorLists";
import FooterButtons from "../footerButtons/FooterButtons";

const formatErrorResponse = (errorResponse: OktaError) => {
  return (
    errorResponse.errorSummary +
    ": " +
    errorResponse.errorCauses.map((error) => error.errorSummary).join(" | ")
  );
};

const MfaEnrollment = ({
  hasActiveFactor,
  transaction,
  setTransaction,
  factorData,
}: {
  hasActiveFactor: boolean;
  transaction: AuthTransaction;
  setTransaction: (transaction: AuthTransaction) => void;
  factorData: ParsedFactorData;
}): ReactElement => {
  const { data: featureFlags } = useGetFeatureFlags();
  const isPasskeyEnabled =
    featureFlags?.["ciam-pim_okta_passkey_enrollment_enabled"] ?? false;

  const filteredFactorData = isPasskeyEnabled
    ? factorData
    : {
        ...factorData,
        factorOrder: factorData.factorOrder.filter(
          (key) => key !== FactorKey.WebAuthn,
        ),
      };

  const [activeFactors, inActiveFactors] = buildFactorLists(filteredFactorData);

  const { mutate: skipMfaEnrollment, isLoading: isSkippingEnrollment } =
    useSkipMfaEnrollment();
  const { mutate: cancelMfaEnrollment, isLoading: isCancellingAuthEnrollment } =
    useCancelMfaEnrollment();

  const skipEnrollment = () => {
    skipMfaEnrollment(
      { stateToken: transaction.data.stateToken },
      {
        onSuccess: (successResponse) => {
          setTransaction(successResponse);
          window.scrollTo(0, 0);
        },
        onError: (errorResponse) => {
          const errors = formatErrorResponse(errorResponse);
          console.log(errors); //temporary
        },
      },
    );
  };

  const skipEnrollmentHandler = () => {
    // If we're in the middle of enrolling in a factor, we need to cancel it (invoke previous transaction)
    // before we can skip the transaction
    if (transaction.status === StatusEnum.MfaEnrollActivate) {
      cancelMfaEnrollment(
        { stateToken: transaction.data.stateToken },
        {
          onSuccess: (transaction) => {
            setTransaction(transaction);
            skipEnrollment();
          },
          onError: (errorResponse) => {
            const errors = formatErrorResponse(errorResponse);
            console.log(errors); //temporary
          },
        },
      );
    } else {
      skipEnrollment();
    }
  };

  const [disableFactorSetup, setDisableFactorSetup] = useState(
    transaction.status === StatusEnum.MfaEnrollActivate,
  );

  const username = transaction.user?.profile?.login ?? "";

  const availableFactorsTitle = hasActiveFactor ? "Available" : undefined;
  return (
    <AuthPageTemplate
      header={{
        title,
        subTitle: <Username username={username} />,
        description,
      }}
    >
      {hasActiveFactor && <FactorList title="Active" factors={activeFactors} />}
      <DisableSetupFactorContext.Provider
        value={{
          disableFactorSetup: disableFactorSetup,
          setDisableFactorSetup: setDisableFactorSetup,
        }}
      >
        <FactorList title={availableFactorsTitle} factors={inActiveFactors} />
      </DisableSetupFactorContext.Provider>

      <FooterButtons>
        <Button
          rightIcon="arrow-right"
          disabled={!hasActiveFactor}
          onClick={skipEnrollmentHandler}
          loading={isSkippingEnrollment || isCancellingAuthEnrollment}
        >
          Next
        </Button>
      </FooterButtons>
    </AuthPageTemplate>
  );
};

export default MfaEnrollment;
